import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionSection = ({ onShowNotification }) => {
  const [currentPlan] = useState({
    name: 'Pro',
    price: 29,
    billingCycle: 'monthly',
    nextBillingDate: '2026-02-07',
    features: [
      'Unlimited prompt generation',
      'All AI tool integrations',
      'Priority support',
      'Advanced analytics',
      'Custom templates',
      'Team collaboration'
    ]
  });

  const [usageStats] = useState({
    promptsGenerated: 247,
    promptsLimit: 1000,
    apiCalls: 1543,
    apiCallsLimit: 5000,
    storageUsed: 2.4,
    storageLimit: 10
  });

  const plans = [
    {
      name: 'Free',
      price: 0,
      billingCycle: 'forever',
      description: 'Perfect for getting started',
      features: [
        '50 prompts per month',
        'Basic AI tools',
        'Community support',
        'Standard templates'
      ],
      current: false
    },
    {
      name: 'Pro',
      price: 29,
      billingCycle: 'monthly',
      description: 'For professionals and creators',
      features: [
        'Unlimited prompts',
        'All AI tool integrations',
        'Priority support',
        'Advanced analytics',
        'Custom templates',
        'Team collaboration'
      ],
      current: true,
      popular: true
    },
    {
      name: 'Enterprise',
      price: 99,
      billingCycle: 'monthly',
      description: 'For teams and organizations',
      features: [
        'Everything in Pro',
        'Dedicated account manager',
        'Custom integrations',
        'Advanced security',
        'SLA guarantee',
        'Unlimited team members'
      ],
      current: false
    }
  ];

  const billingHistory = [
    {
      id: 1,
      date: '2026-01-07',
      amount: 29,
      status: 'paid',
      invoice: 'INV-2026-001'
    },
    {
      id: 2,
      date: '2025-12-07',
      amount: 29,
      status: 'paid',
      invoice: 'INV-2025-012'
    },
    {
      id: 3,
      date: '2025-11-07',
      amount: 29,
      status: 'paid',
      invoice: 'INV-2025-011'
    }
  ];

  const handleUpgrade = (planName) => {
    onShowNotification('success', 'Plan Updated', `Successfully upgraded to ${planName} plan.`);
  };

  const handleCancelSubscription = () => {
    onShowNotification('warning', 'Subscription Cancelled', 'Your subscription will remain active until the end of the billing period.');
  };

  const handleDownloadInvoice = (invoice) => {
    onShowNotification('success', 'Invoice Downloaded', `Invoice ${invoice} has been downloaded.`);
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">Subscription & Billing</h2>
        <p className="text-sm md:text-base text-muted-foreground mt-1">Manage your subscription plan and billing information</p>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 md:p-8">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-2xl font-bold text-foreground">{currentPlan?.name} Plan</h3>
              <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                Active
              </span>
            </div>
            <p className="text-muted-foreground">
              ${currentPlan?.price}/{currentPlan?.billingCycle} • Next billing: {currentPlan?.nextBillingDate}
            </p>
          </div>
          <Button variant="outline" iconName="CreditCard" iconPosition="left">
            Update Payment Method
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentPlan?.features?.map((feature, index) => (
            <div key={index} className="flex items-center gap-2">
              <Icon name="Check" size={18} color="var(--color-success)" />
              <span className="text-sm text-foreground">{feature}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 md:p-8">
        <h3 className="text-lg font-semibold text-foreground mb-6">Usage Statistics</h3>
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-foreground">Prompts Generated</span>
              <span className="text-sm font-medium text-foreground">
                {usageStats?.promptsGenerated} / {usageStats?.promptsLimit}
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${(usageStats?.promptsGenerated / usageStats?.promptsLimit) * 100}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-foreground">API Calls</span>
              <span className="text-sm font-medium text-foreground">
                {usageStats?.apiCalls} / {usageStats?.apiCallsLimit}
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-secondary rounded-full transition-all"
                style={{ width: `${(usageStats?.apiCalls / usageStats?.apiCallsLimit) * 100}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-foreground">Storage Used</span>
              <span className="text-sm font-medium text-foreground">
                {usageStats?.storageUsed} GB / {usageStats?.storageLimit} GB
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-accent rounded-full transition-all"
                style={{ width: `${(usageStats?.storageUsed / usageStats?.storageLimit) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 md:mb-6">Available Plans</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {plans?.map((plan) => (
            <div
              key={plan?.name}
              className={`bg-card rounded-lg border p-6 md:p-8 ${
                plan?.popular ? 'border-primary shadow-lg' : 'border-border'
              } ${plan?.current ? 'opacity-60' : ''}`}
            >
              {plan?.popular && (
                <span className="inline-block px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full mb-4">
                  Most Popular
                </span>
              )}
              <h4 className="text-xl font-bold text-foreground mb-2">{plan?.name}</h4>
              <div className="mb-4">
                <span className="text-3xl font-bold text-foreground">${plan?.price}</span>
                <span className="text-muted-foreground">/{plan?.billingCycle}</span>
              </div>
              <p className="text-sm text-muted-foreground mb-6">{plan?.description}</p>
              <ul className="space-y-3 mb-6">
                {plan?.features?.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Icon name="Check" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant={plan?.current ? 'outline' : plan?.popular ? 'default' : 'outline'}
                disabled={plan?.current}
                onClick={() => handleUpgrade(plan?.name)}
                fullWidth
              >
                {plan?.current ? 'Current Plan' : 'Upgrade'}
              </Button>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 md:p-8">
        <h3 className="text-lg font-semibold text-foreground mb-6">Billing History</h3>
        <div className="overflow-x-auto -mx-6 md:-mx-8">
          <div className="inline-block min-w-full align-middle px-6 md:px-8">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left py-3 text-sm font-medium text-muted-foreground">Invoice</th>
                  <th className="text-left py-3 text-sm font-medium text-muted-foreground">Amount</th>
                  <th className="text-left py-3 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-right py-3 text-sm font-medium text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody>
                {billingHistory?.map((item) => (
                  <tr key={item?.id} className="border-b border-border last:border-b-0">
                    <td className="py-4 text-sm text-foreground">{item?.date}</td>
                    <td className="py-4 text-sm text-foreground">{item?.invoice}</td>
                    <td className="py-4 text-sm text-foreground">${item?.amount}</td>
                    <td className="py-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
                        <Icon name="CheckCircle" size={12} />
                        {item?.status}
                      </span>
                    </td>
                    <td className="py-4 text-right">
                      <button
                        onClick={() => handleDownloadInvoice(item?.invoice)}
                        className="text-primary hover:text-primary/80 text-sm font-medium transition-colors"
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-destructive p-6 md:p-8">
        <div className="flex items-start gap-3 mb-4">
          <Icon name="AlertTriangle" size={24} color="var(--color-destructive)" className="flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-destructive">Cancel Subscription</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Your subscription will remain active until {currentPlan?.nextBillingDate}
            </p>
          </div>
        </div>
        <Button variant="destructive" iconName="XCircle" iconPosition="left" onClick={handleCancelSubscription}>
          Cancel Subscription
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionSection;